import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import * as XLSX from "xlsx";

const API_GIFT_BALANCES_LIST = "/api/gift-balances-list.php";
const API_GIFT_AWARD_LIST = "/api/gift-award-list.php";

const MONTHS_TR = [
  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"
];

function safeNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function formatNumber(value) {
  return safeNumber(value).toLocaleString("tr-TR");
}

function defaultMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function periodLabel(periodMonth) {
  const match = String(periodMonth || "").match(/^(\d{4})-(\d{2})$/);
  if (!match) return periodMonth || "-";
  return `${MONTHS_TR[Number(match[2]) - 1] || match[2]} ${match[1]}`;
}

const LOCAL_DISPATCHES_KEY = "wolf_local_gift_dispatches_v1";

function getLocalDispatches() {
  try {
    const list = JSON.parse(localStorage.getItem(LOCAL_DISPATCHES_KEY) || "[]");
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

function applyLocalDispatchesToBalances(items = [], period = "") {
  const localList = getLocalDispatches().filter((d) => d.status !== "cancelled");
  if (!localList.length) return items;

  return items.map((row) => {
    const periodLocal = localList.filter((d) => d.publisherUsername === row.publisherUsername && d.periodMonth === period && d.isLocal);
    const periodLocalSum = periodLocal.reduce((acc, d) => acc + Number(d.diamonds || 0), 0);

    const allLocal = localList.filter((d) => d.publisherUsername === row.publisherUsername && d.isLocal);
    const allLocalSum = allLocal.reduce((acc, d) => acc + Number(d.diamonds || 0), 0);

    if (periodLocalSum === 0 && allLocalSum === 0) return row;

    const newPeriodDispatched = (row.periodDispatched || 0) + periodLocalSum;
    const newPeriodNetMovement = (row.periodAward || 0) - newPeriodDispatched;
    const newClosingBalance = (row.carriedBalance || 0) + newPeriodNetMovement;
    const newCurrentTotalBalance = (row.currentTotalBalance || 0) - allLocalSum;

    return {
      ...row,
      periodDispatched: newPeriodDispatched,
      periodNetMovement: newPeriodNetMovement,
      closingBalance: newClosingBalance,
      currentTotalBalance: newCurrentTotalBalance,
      statusKey: newCurrentTotalBalance > 0 ? "RECEIVABLE" : newCurrentTotalBalance < 0 ? "DEBTOR" : "ZERO",
      statusLabel: newCurrentTotalBalance > 0 ? "Alacaklı Yayıncı" : newCurrentTotalBalance < 0 ? "Borçlu / Fazla Gönderim" : "Hesap Dengede",
    };
  });
}

async function requestJson(url, options = {}) {
  try {
    const response = await fetch(url, options);
    const raw = await response.text();
    if (!raw || !raw.trim()) {
      return {
        success: false,
        message: "Sunucudan boş yanıt döndü (HTTP " + (response.status || 500) + ")."
      };
    }
    const trimmed = raw.trim().toLowerCase();
    if (trimmed.startsWith("<!doctype") || trimmed.startsWith("<html") || trimmed.includes("<html")) {
      return {
        success: false,
        isHtmlFallback: true,
        message: "API endpoint yanıtı bekleniyor (HTML yanıtı alındı)."
      };
    }
    try {
      return JSON.parse(raw);
    } catch (parseErr) {
      return {
        success: false,
        message: "Sunucudan geçersiz veri biçimi alındı: " + (raw.length > 80 ? raw.substring(0, 80) + "..." : raw)
      };
    }
  } catch (err) {
    return { success: false, message: err?.message || "Sunucuyla bağlantı kurulamadı." };
  }
}

export function PublisherBalancesContent({
  publisherRows = [],
  managerRows = [],
  currentAdmin = {},
}) {
  // En son yüklenen dönemi hafızadan veya varsayılandan al
  const [periodMonth, setPeriodMonth] = useState(() => {
    try {
      const storedReports = JSON.parse(localStorage.getItem("wolf_tiktok_activity_monthly_reports_v1") || "{}");
      const months = Object.keys(storedReports).sort().reverse();
      if (months.length > 0) return months[0];
    } catch {}
    return "2026-09";
  });

  const [search, setSearch] = useState("");
  const [balanceFilter, setBalanceFilter] = useState("all"); // 'all', 'positive', 'negative', 'zero'
  const [sortBy, setSortBy] = useState("default");
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const filterPanelRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [globalSummary, setGlobalSummary] = useState(null);
  const [availablePeriods, setAvailablePeriods] = useState(["2026-09"]);
  const [latestUploadedPeriod, setLatestUploadedPeriod] = useState("2026-09");
  const [banner, setBanner] = useState(null);
  const initialLoadedRef = useRef(false);

  // Ekstre / Hareket Çekmecesi (Drawer)
  const [ledgerModal, setLedgerModal] = useState({
    open: false,
    publisher: null,
    loading: false,
    ledger: [],
    currentTotalBalance: 0,
  });

  // Dış tıklamada filtre panelini kapat
  useEffect(() => {
    function handleClickOutside(event) {
      if (filterPanelRef.current && !filterPanelRef.current.contains(event.target)) {
        setFilterPanelOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Verileri Yükle (Kararlı, Döngüsüz)
  const loadData = useCallback(async (targetPeriod) => {
    if (!targetPeriod) return;
    try {
      setLoading(true);
      // 1. Önce özel bakiye API'sini dene
      let res = await requestJson(
        `${API_GIFT_BALANCES_LIST}?periodMonth=${encodeURIComponent(targetPeriod)}`,
        { cache: "no-store" }
      );

      // 2. Fallback: Eğer API HTML döndüyse (dosya henüz sunucuda açılmamışsa), standart gift-award-list'ten bakiye oluştur!
      if (!res || !res.success || res.isHtmlFallback) {
        const fallbackRes = await requestJson(
          `${API_GIFT_AWARD_LIST}?periodMonth=${encodeURIComponent(targetPeriod)}`,
          { cache: "no-store" }
        );

        if (fallbackRes && fallbackRes.success && Array.isArray(fallbackRes.data)) {
          const rawAwards = fallbackRes.data;
          let totalAwardsDiamonds = 0;
          const mappedRows = rawAwards.map((ar) => {
            const u = String(ar.publisher_username || "").replace("@", "").trim();
            let rawDiamonds = safeNumber(ar.award_diamonds || ar.diamonds);
            if (rawDiamonds <= 0) {
              const gDetail = String(ar.gift_detail || "");
              const aNote = String(ar.admin_note || "");
              if (aNote.includes("Elmas") && /Admin:\s*([\d.]+)\s*Elmas/i.test(aNote)) {
                const match = aNote.match(/Admin:\s*([\d.]+)\s*Elmas/i);
                rawDiamonds = parseInt(match[1].replace(/\./g, ""), 10);
              } else if (/(\d+(?:[.,]\d+)?)\s*K\s*Elmas/i.test(gDetail)) {
                const match = gDetail.match(/(\d+(?:[.,]\d+)?)\s*K\s*Elmas/i);
                rawDiamonds = Math.round(parseFloat(match[1].replace(",", ".")) * 1000);
              } else if (/(\d+)\s*Elmas/i.test(gDetail)) {
                const match = gDetail.match(/(\d+)\s*Elmas/i);
                rawDiamonds = parseInt(match[1], 10);
              }
            }

            const isCancelled = ar.publish_status === "Yayından Kaldırıldı" && ar.delivery_status === "İptal";
            const effectiveDiamonds = isCancelled ? 0 : rawDiamonds;
            totalAwardsDiamonds += effectiveDiamonds;

            return {
              publisherUsername: u,
              publisherName: ar.publisher_name || u,
              hiddenId: ar.hidden_id || "-",
              periodMonth: targetPeriod,
              carriedBalance: 0,
              periodAward: effectiveDiamonds,
              isAwardCancelled: isCancelled,
              periodDispatched: 0,
              periodNetMovement: effectiveDiamonds,
              closingBalance: effectiveDiamonds,
              currentTotalBalance: effectiveDiamonds,
              statusKey: effectiveDiamonds > 0 ? "RECEIVABLE" : "ZERO",
              statusLabel: effectiveDiamonds > 0 ? "Alacaklı Yayıncı" : "Hesap Dengede",
            };
          });

          const adjustedRows = applyLocalDispatchesToBalances(mappedRows, targetPeriod);
          setRows(adjustedRows);
          setGlobalSummary({
            totalPublishers: adjustedRows.length,
            selectedPeriodCarriedTotal: 0,
            selectedPeriodAwardTotal: totalAwardsDiamonds,
            selectedPeriodDispatchedTotal: adjustedRows.reduce((a, b) => a + (b.periodDispatched || 0), 0),
            selectedPeriodNetMovementTotal: adjustedRows.reduce((a, b) => a + (b.periodNetMovement || 0), 0),
            selectedPeriodClosingTotal: adjustedRows.reduce((a, b) => a + (b.closingBalance || 0), 0),
            currentGlobalTotalBalance: adjustedRows.reduce((a, b) => a + (b.currentTotalBalance || 0), 0),
            positiveBalanceCount: adjustedRows.filter((r) => r.currentTotalBalance > 0).length,
            negativeBalanceCount: adjustedRows.filter((r) => r.currentTotalBalance < 0).length,
            zeroBalanceCount: adjustedRows.filter((r) => r.currentTotalBalance === 0).length,
          });

          setAvailablePeriods((prev) => {
            if (prev.includes("2026-09")) return prev;
            return [...prev, "2026-09"];
          });
          return;
        }
      }

      // 3. Normal başarılı yanıt işleme
      if (res && res.success) {
        const items = Array.isArray(res.items) ? res.items : [];
        const adjustedItems = applyLocalDispatchesToBalances(items, targetPeriod);
        setRows(adjustedItems);
        setGlobalSummary(res.summary || null);

        if (Array.isArray(res.availablePeriods) && res.availablePeriods.length > 0) {
          setAvailablePeriods((prev) => {
            const next = res.availablePeriods;
            if (prev.length === next.length && prev.every((v, i) => v === next[i])) return prev;
            return next;
          });
          setLatestUploadedPeriod((prev) => {
            const next = res.availablePeriods[0];
            return prev === next ? prev : next;
          });

          if (!initialLoadedRef.current) {
            initialLoadedRef.current = true;
            if (items.length === 0 && res.availablePeriods[0] && targetPeriod !== res.availablePeriods[0]) {
              setPeriodMonth(res.availablePeriods[0]);
            }
          }
        }
      }
    } catch (err) {
      console.warn("Bakiye verileri yüklenirken hata:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData(periodMonth);
  }, [periodMonth, loadData]);

  // Filtrelenmiş Satırlar
  const filteredRows = useMemo(() => {
    let result = [...rows];
    if (balanceFilter === "positive") {
      result = result.filter((r) => r.currentTotalBalance > 0);
    } else if (balanceFilter === "negative") {
      result = result.filter((r) => r.currentTotalBalance < 0);
    } else if (balanceFilter === "zero") {
      result = result.filter((r) => r.currentTotalBalance === 0);
    }

    if (search.trim()) {
      const q = search.toLowerCase().trim();
      result = result.filter(
        (r) =>
          String(r.publisherUsername || "").toLowerCase().includes(q) ||
          String(r.publisherName || "").toLowerCase().includes(q) ||
          String(r.hiddenId || "").toLowerCase().includes(q)
      );
    }

    if (sortBy === "balance_desc") {
      result.sort((a, b) => b.currentTotalBalance - a.currentTotalBalance);
    } else if (sortBy === "balance_asc") {
      result.sort((a, b) => a.currentTotalBalance - b.currentTotalBalance);
    } else if (sortBy === "name_asc") {
      result.sort((a, b) => (a.publisherName || "").localeCompare(b.publisherName || ""));
    }

    return result;
  }, [rows, balanceFilter, search, sortBy]);

  const activeFiltersCount = (balanceFilter !== "all" ? 1 : 0) + (search.trim() ? 1 : 0) + (sortBy !== "default" ? 1 : 0);

  const handleClearFilters = () => {
    setBalanceFilter("all");
    setSearch("");
    setSortBy("default");
    setFilterPanelOpen(false);
  };

  // Yayıncı Hesap Ekstresini Getir
  const handleOpenLedger = async (row) => {
    setLedgerModal({
      open: true,
      publisher: row,
      loading: true,
      ledger: [],
      currentTotalBalance: row.currentTotalBalance,
    });

    try {
      const res = await requestJson(
        `${API_GIFT_BALANCES_LIST}?username=${encodeURIComponent(row.publisherUsername)}`,
        { cache: "no-store" }
      );
      if (res && res.success) {
        setLedgerModal((prev) => ({
          ...prev,
          loading: false,
          ledger: Array.isArray(res.ledger) ? res.ledger : [],
          currentTotalBalance: res.currentTotalBalance ?? prev.currentTotalBalance,
        }));
      } else {
        // Fallback: tek bir hakediş hareketi göster
        setLedgerModal((prev) => ({
          ...prev,
          loading: false,
          ledger: [
            {
              type: "AWARD",
              id: 1,
              periodMonth: row.periodMonth,
              transactionDate: "2026-09-30",
              description: `${periodLabel(row.periodMonth)} Hediye Hakedişi`,
              amount: row.periodAward,
              isCancelled: row.isAwardCancelled,
              runningBalance: row.periodAward,
              createdBy: "admin",
            },
          ],
        }));
      }
    } catch (err) {
      setLedgerModal((prev) => ({ ...prev, loading: false }));
    }
  };

  // Excel Dışa Aktar
  const handleExportExcel = () => {
    if (!filteredRows.length) return;
    const exportData = filteredRows.map((r, i) => ({
      No: i + 1,
      "Yayıncı Adı": r.publisherName,
      "Kullanıcı Adı": `@${r.publisherUsername}`,
      "Hidden ID": r.hiddenId,
      Dönem: periodLabel(r.periodMonth),
      "Devreden Bakiye (Elmas)": r.carriedBalance,
      "Seçili Ay Hakedişi (+)": r.periodAward,
      "Seçili Ay Gönderimleri (−)": r.periodDispatched,
      "Seçili Ay Net Hareketi": r.periodNetMovement,
      "Seçili Ay Kapanış Bakiyesi": r.closingBalance,
      "GÜNCEL TOPLAM BAKİYE (Elmas)": r.currentTotalBalance,
      "Bakiye Statüsü": r.statusLabel,
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Yayinici_Bakiyeleri");
    XLSX.writeFile(wb, `Wolf_Media_Yayinici_Bakiyeleri_${periodMonth}.xlsx`);
  };

  return (
    <div className="space-y-5">
      {/* Banner */}
      {banner && (
        <div
          className={`flex items-center justify-between rounded-xl border p-3.5 text-xs font-semibold transition ${
            banner.type === "success"
              ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
              : "border-amber-500/40 bg-amber-500/10 text-amber-200"
          }`}
        >
          <span>{banner.message}</span>
          <button
            type="button"
            onClick={() => setBanner(null)}
            className="text-white/60 hover:text-white font-black ml-4"
          >
            ✕
          </button>
        </div>
      )}

      {/* SADELEŞTİRİLMİŞ ÜST ALAN: BAŞLIK, GENEL TARİH SEÇİCİ VE İŞLEMLER */}
      <div className="rounded-2xl border border-white/10 bg-[#0d0d10] p-4 shadow-xl">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="text-[10px] font-black uppercase tracking-[0.25em] text-yellow-400/90">
              Wolf Media • Bakiye & Cari Hesap Yönetimi
            </div>
            <h2 className="mt-0.5 text-2xl font-black text-white tracking-tight">
              Wolf Media Yayıncı Bakiyeleri
            </h2>
            <div className="mt-1 flex items-center gap-2 text-xs text-white/50">
              <span>Seçili Dönem:</span>
              <span className="font-bold text-yellow-300">{periodLabel(periodMonth)}</span>
              <span>•</span>
              <span className="text-white/70">{rows.length} Yayıncı Cari Hesabı</span>
            </div>
          </div>

          {/* GENEL TARİH VE İŞLEM GRUBU */}
          <div className="flex flex-wrap items-center gap-2.5">
            {/* Genel Dönem Seçici (Kompakt) */}
            <div className="flex items-center gap-1.5 rounded-xl border border-white/10 bg-black/60 p-1">
              <span className="pl-2 text-xs text-yellow-400">📅</span>
              <select
                value={periodMonth}
                onChange={(e) => setPeriodMonth(e.target.value)}
                className="rounded-lg bg-transparent px-2 py-1.5 text-xs font-bold text-white outline-none [color-scheme:dark]"
              >
                {availablePeriods.map((pm) => (
                  <option key={pm} value={pm} className="bg-[#121215]">
                    {periodLabel(pm)}
                  </option>
                ))}
                {!availablePeriods.includes(periodMonth) && (
                  <option value={periodMonth} className="bg-[#121215]">
                    {periodLabel(periodMonth)}
                  </option>
                )}
              </select>

              {/* Son Yüklenen Dönem Kısayolu */}
              {latestUploadedPeriod && periodMonth !== latestUploadedPeriod && (
                <button
                  type="button"
                  onClick={() => setPeriodMonth(latestUploadedPeriod)}
                  className="rounded-lg bg-yellow-500/20 px-2 py-1 text-[11px] font-black text-yellow-300 hover:bg-yellow-500 hover:text-black transition"
                  title="En son veri yüklenen döneme git"
                >
                  ⚡ {periodLabel(latestUploadedPeriod)}
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={handleExportExcel}
              className="inline-flex items-center gap-1.5 rounded-xl border border-white/10 bg-white/[0.04] px-3.5 py-2 text-xs font-bold text-white/80 hover:text-white hover:border-yellow-500/30 transition"
            >
              <span>📥</span>
              <span>Excel Raporu</span>
            </button>

            <button
              type="button"
              onClick={() => loadData(periodMonth)}
              disabled={loading}
              className="inline-flex items-center gap-1.5 rounded-xl border border-white/10 bg-white/[0.04] px-3.5 py-2 text-xs font-bold text-white/80 hover:text-yellow-300 transition disabled:opacity-45"
            >
              <span className={loading ? "animate-spin" : ""}>🔄</span>
              <span>Yenile</span>
            </button>
          </div>
        </div>
      </div>

      {/* KPI KARTLARI (DEVİR, SEÇİLİ AY VE GÜNCEL TOPLAM BAKİYE VURGUSU) */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 lg:gap-3.5">
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3.5">
          <div className="text-[10px] font-black uppercase text-white/40">Önceki Dönemden Devir</div>
          <div className="mt-1 text-lg font-black text-white/80">
            💎 {formatNumber(globalSummary?.selectedPeriodCarriedTotal || 0)}
          </div>
          <div className="text-[11px] text-white/40">Seçili ay öncesi net</div>
        </div>

        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3.5">
          <div className="text-[10px] font-black uppercase text-white/40">Seçili Ay Hakediş (+)</div>
          <div className="mt-1 text-lg font-black text-yellow-300">
            💎 {formatNumber(globalSummary?.selectedPeriodAwardTotal || 0)}
          </div>
          <div className="text-[11px] text-yellow-500/60">Hak kazanılan hediye</div>
        </div>

        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3.5">
          <div className="text-[10px] font-black uppercase text-white/40">Seçili Ay Gönderim (−)</div>
          <div className="mt-1 text-lg font-black text-emerald-300">
            🎁 {formatNumber(globalSummary?.selectedPeriodDispatchedTotal || 0)}
          </div>
          <div className="text-[11px] text-emerald-400/60">Döneme bağlanan</div>
        </div>

        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3.5">
          <div className="text-[10px] font-black uppercase text-white/40">Seçili Ay Kapanış</div>
          <div
            className={`mt-1 text-lg font-black ${
              (globalSummary?.selectedPeriodClosingTotal || 0) > 0
                ? "text-yellow-300"
                : (globalSummary?.selectedPeriodClosingTotal || 0) < 0
                ? "text-red-400"
                : "text-zinc-400"
            }`}
          >
            {(globalSummary?.selectedPeriodClosingTotal || 0) > 0 ? "+" : ""}
            {formatNumber(globalSummary?.selectedPeriodClosingTotal || 0)}
          </div>
          <div className="text-[11px] text-white/40">Devreden + Net Hareket</div>
        </div>

        {/* GÜNCEL TOPLAM BAKİYE (ALTIN VURGULU ÖZEL KART) */}
        <div className="col-span-2 sm:col-span-1 rounded-xl border border-yellow-500/40 bg-gradient-to-br from-yellow-500/15 via-black/40 to-yellow-500/5 p-3.5 shadow-[0_0_20px_rgba(234,179,8,0.12)]">
          <div className="text-[10px] font-black uppercase text-yellow-300 flex items-center gap-1">
            <span>⭐</span>
            <span>GÜNCEL TOPLAM BAKİYE</span>
          </div>
          <div
            className={`mt-1 text-xl font-black ${
              (globalSummary?.currentGlobalTotalBalance || 0) >= 0 ? "text-yellow-300" : "text-red-400"
            }`}
          >
            {(globalSummary?.currentGlobalTotalBalance || 0) > 0 ? "+" : ""}
            💎 {formatNumber(globalSummary?.currentGlobalTotalBalance || 0)}
          </div>
          <div className="text-[11px] text-yellow-200/60 font-semibold">Tüm dönemlerin toplamı</div>
        </div>
      </div>

      {/* SADELEŞTİRİLMİŞ FİLTRE VE ARAMA ÇUBUĞU ("FİLTRELER" DÜĞMESİ İLE) */}
      <div className="rounded-xl border border-white/10 bg-[#0d0d10] p-3 shadow-md">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-1">
            <div className="relative flex-1 max-w-md">
              <span className="absolute left-3 top-2.5 text-xs text-white/30">🔍</span>
              <input
                type="text"
                placeholder="Yayıncı ara (İsim, @kullanıcı, Hidden ID)..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-xl border border-white/10 bg-black/60 pl-8 pr-3 py-2 text-xs text-white placeholder-white/30 outline-none focus:border-yellow-500/40"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="absolute right-3 top-2.5 text-xs text-white/40 hover:text-white"
                >
                  ✕
                </button>
              )}
            </div>

            {/* TEK BİR "FİLTRELER" DÜĞMESİ */}
            <div className="relative" ref={filterPanelRef}>
              <button
                type="button"
                onClick={() => setFilterPanelOpen((prev) => !prev)}
                className={`inline-flex items-center gap-1.5 rounded-xl border px-3.5 py-2 text-xs font-bold transition ${
                  activeFiltersCount > 0
                    ? "border-yellow-500/50 bg-yellow-500/20 text-yellow-300 shadow-sm"
                    : "border-white/10 bg-white/[0.04] text-white/70 hover:text-white"
                }`}
              >
                <span>⚙️ Filtreler</span>
                {activeFiltersCount > 0 && (
                  <span className="rounded-full bg-yellow-400 px-1.5 py-0.2 text-[10px] font-black text-black">
                    {activeFiltersCount}
                  </span>
                )}
                <span className="text-[10px] opacity-60">▼</span>
              </button>

              {/* FİLTRE PANELİ POPOVER */}
              {filterPanelOpen && (
                <div className="absolute left-0 sm:left-auto sm:right-0 mt-2 w-72 rounded-2xl border border-white/15 bg-[#141418] p-4 shadow-2xl z-40 text-white animate-in fade-in duration-100">
                  <div className="flex items-center justify-between border-b border-white/10 pb-2.5 mb-3">
                    <span className="text-xs font-black uppercase text-yellow-300">Bakiye Filtreleri</span>
                    {activeFiltersCount > 0 && (
                      <button
                        type="button"
                        onClick={handleClearFilters}
                        className="text-[11px] font-bold text-red-300 hover:text-red-200"
                      >
                        Temizle
                      </button>
                    )}
                  </div>

                  {/* Bakiye Durumu */}
                  <div className="space-y-1 mb-3">
                    <label className="text-[11px] font-bold text-white/60 block">Bakiye Durumu</label>
                    <select
                      value={balanceFilter}
                      onChange={(e) => setBalanceFilter(e.target.value)}
                      className="w-full rounded-xl border border-white/10 bg-black/60 px-2.5 py-1.5 text-xs text-white outline-none focus:border-yellow-500/40 [color-scheme:dark]"
                    >
                      <option value="all">Tümü (Filtresiz)</option>
                      <option value="positive">🟢 Alacaklı Yayıncılar (+)</option>
                      <option value="negative">🔴 Fazla Gönderildi / Mahsup (−)</option>
                      <option value="zero">⚪ Hesap Dengede (0)</option>
                    </select>
                  </div>

                  {/* Sıralama */}
                  <div className="space-y-1 mb-4">
                    <label className="text-[11px] font-bold text-white/60 block">Sıralama</label>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="w-full rounded-xl border border-white/10 bg-black/60 px-2.5 py-1.5 text-xs text-white outline-none focus:border-yellow-500/40 [color-scheme:dark]"
                    >
                      <option value="default">Varsayılan</option>
                      <option value="balance_desc">En Yüksek Bakiye 💎</option>
                      <option value="balance_asc">En Düşük Bakiye 💎</option>
                      <option value="name_asc">Yayıncı Adı (A-Z)</option>
                    </select>
                  </div>

                  <button
                    type="button"
                    onClick={() => setFilterPanelOpen(false)}
                    className="w-full rounded-xl bg-yellow-500 py-2 text-xs font-black text-black hover:bg-yellow-400 transition"
                  >
                    Uygula
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="text-xs text-white/50 text-right">
            <span>Listelenen: </span>
            <span className="font-bold text-white">{filteredRows.length}</span>
            <span> / {rows.length} Yayıncı</span>
          </div>
        </div>

        {/* AKTİF FİLTRE ETİKETLERİ */}
        {activeFiltersCount > 0 && (
          <div className="mt-2.5 flex flex-wrap items-center gap-1.5 pt-2 border-t border-white/5">
            <span className="text-[11px] text-white/40 font-semibold mr-1">Aktif Filtreler:</span>
            {search.trim() && (
              <span className="inline-flex items-center gap-1 rounded-lg border border-yellow-500/30 bg-yellow-500/10 px-2 py-0.5 text-[11px] text-yellow-300">
                <span>Ara: "{search}"</span>
                <button type="button" onClick={() => setSearch("")} className="hover:text-white font-bold ml-0.5">✕</button>
              </span>
            )}
            {balanceFilter !== "all" && (
              <span className="inline-flex items-center gap-1 rounded-lg border border-yellow-500/30 bg-yellow-500/10 px-2 py-0.5 text-[11px] text-yellow-300">
                <span>
                  Durum: {balanceFilter === "positive" ? "Alacaklı" : balanceFilter === "negative" ? "Mahsup" : "Dengede"}
                </span>
                <button type="button" onClick={() => setBalanceFilter("all")} className="hover:text-white font-bold ml-0.5">✕</button>
              </span>
            )}
            {sortBy !== "default" && (
              <span className="inline-flex items-center gap-1 rounded-lg border border-yellow-500/30 bg-yellow-500/10 px-2 py-0.5 text-[11px] text-yellow-300">
                <span>Sıralama: {sortBy}</span>
                <button type="button" onClick={() => setSortBy("default")} className="hover:text-white font-bold ml-0.5">✕</button>
              </span>
            )}
            <button
              type="button"
              onClick={handleClearFilters}
              className="text-[11px] font-bold text-red-300 hover:text-red-200 underline ml-2"
            >
              Filtreleri Temizle
            </button>
          </div>
        )}
      </div>

      {/* ANA BAKİYE TABLOSU */}
      <div className="rounded-2xl border border-white/10 bg-[#09090b] shadow-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left text-xs">
            <thead>
              <tr className="border-b border-white/10 bg-white/[0.02] text-[11px] font-black uppercase tracking-wider text-white/40">
                <th className="py-3.5 px-4 w-12 text-center">#</th>
                <th className="py-3.5 px-4">Yayıncı Bilgisi</th>
                <th className="py-3.5 px-4 text-right">Devreden Bakiye</th>
                <th className="py-3.5 px-4 text-right">Seçili Ay Hakediş (+)</th>
                <th className="py-3.5 px-4 text-right">Seçili Ay Gönderim (−)</th>
                <th className="py-3.5 px-4 text-right">Seçili Ay Net</th>
                <th className="py-3.5 px-4 text-right">Seçili Ay Kapanış</th>
                <th className="py-3.5 px-4 text-right bg-yellow-500/5 border-l border-r border-yellow-500/20">
                  <span className="text-yellow-300">GÜNCEL TOPLAM BAKİYE</span>
                </th>
                <th className="py-3.5 px-4 text-center">Durum</th>
                <th className="py-3.5 px-4 text-right">Ekstre</th>
              </tr>
            </thead>
            <tbody className={`divide-y divide-white/[0.06] transition-opacity duration-150 ${loading && rows.length > 0 ? "opacity-60" : "opacity-100"}`}>
              {loading && rows.length === 0 ? (
                <tr>
                  <td colSpan={10} className="py-12 text-center text-white/50">
                    <span className="animate-spin inline-block mr-2">🔄</span>
                    Yayıncı bakiyeleri hesaplanıyor...
                  </td>
                </tr>
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={10} className="py-12 text-center text-white/40">
                    {periodLabel(periodMonth)} dönemi için bakiye kaydı bulunamadı.
                  </td>
                </tr>
              ) : (
                filteredRows.map((r, idx) => {
                  const carried = r.carriedBalance;
                  const award = r.periodAward;
                  const disp = r.periodDispatched;
                  const netMov = r.periodNetMovement;
                  const closing = r.closingBalance;
                  const total = r.currentTotalBalance;

                  return (
                    <tr
                      key={r.publisherUsername}
                      className="hover:bg-white/[0.02] transition-colors"
                    >
                      <td className="py-3.5 px-4 text-center text-white/30 font-bold">
                        {idx + 1}
                      </td>

                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-yellow-500/25 bg-yellow-500/10 text-xs font-black text-yellow-300">
                            {(r.publisherName || r.publisherUsername || "W")[0].toUpperCase()}
                          </div>
                          <div>
                            <div className="font-bold text-white text-sm">
                              {r.publisherName || r.publisherUsername}
                            </div>
                            <div className="flex items-center gap-2 text-[11px] text-white/40">
                              <span>@{r.publisherUsername}</span>
                              {r.hiddenId && r.hiddenId !== "-" && (
                                <>
                                  <span>•</span>
                                  <span className="font-mono text-white/30">ID: {r.hiddenId}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="py-3.5 px-4 text-right font-bold text-white/70">
                        {carried > 0 ? `+${formatNumber(carried)}` : formatNumber(carried)}
                      </td>

                      <td className="py-3.5 px-4 text-right font-black text-yellow-300">
                        {award > 0 ? `+${formatNumber(award)}` : "0"}
                      </td>

                      <td className="py-3.5 px-4 text-right font-black text-emerald-300">
                        {disp > 0 ? `-${formatNumber(disp)}` : "0"}
                      </td>

                      <td className="py-3.5 px-4 text-right">
                        <span
                          className={`font-black ${
                            netMov > 0
                              ? "text-yellow-300"
                              : netMov < 0
                              ? "text-red-400"
                              : "text-zinc-400"
                          }`}
                        >
                          {netMov > 0 ? `+${formatNumber(netMov)}` : formatNumber(netMov)}
                        </span>
                      </td>

                      <td className="py-3.5 px-4 text-right font-bold">
                        <span
                          className={
                            closing > 0
                              ? "text-yellow-300"
                              : closing < 0
                              ? "text-red-400"
                              : "text-zinc-400"
                          }
                        >
                          {closing > 0 ? `+${formatNumber(closing)}` : formatNumber(closing)}
                        </span>
                      </td>

                      <td className="py-3.5 px-4 text-right bg-yellow-500/5 border-l border-r border-yellow-500/20">
                        <div className="flex flex-col items-end">
                          <span
                            className={`text-sm font-black ${
                              total > 0
                                ? "text-yellow-300"
                                : total < 0
                                ? "text-red-400"
                                : "text-zinc-400"
                            }`}
                          >
                            {total > 0 ? `+${formatNumber(total)}` : formatNumber(total)}
                          </span>
                          <span className="text-[10px] text-white/40 font-semibold">Elmas</span>
                        </div>
                      </td>

                      <td className="py-3.5 px-4 text-center">
                        {total > 0 ? (
                          <span className="inline-flex items-center gap-1 rounded-full border border-yellow-500/30 bg-yellow-500/10 px-2.5 py-0.5 text-[11px] font-bold text-yellow-300">
                            🟢 Alacaklı
                          </span>
                        ) : total < 0 ? (
                          <span className="inline-flex items-center gap-1 rounded-full border border-red-500/30 bg-red-500/10 px-2.5 py-0.5 text-[11px] font-bold text-red-300">
                            🔴 Mahsup
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full border border-zinc-700 bg-zinc-800 px-2.5 py-0.5 text-[11px] font-bold text-zinc-400">
                            ⚪ Dengede
                          </span>
                        )}
                      </td>

                      <td className="py-3.5 px-4 text-right">
                        <button
                          type="button"
                          onClick={() => handleOpenLedger(r)}
                          className="rounded-lg border border-yellow-500/30 bg-yellow-500/10 px-3 py-1.5 text-xs font-bold text-yellow-300 hover:bg-yellow-500 hover:text-black transition"
                        >
                          📋 Ekstre
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* SLIDE-OVER MODAL: HESAP EKSTRESİ */}
      {ledgerModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-end bg-black/80 backdrop-blur-sm">
          <div className="h-full w-full max-w-2xl border-l border-white/15 bg-[#101014] p-6 shadow-2xl text-white flex flex-col justify-between overflow-hidden animate-in slide-in-from-right duration-200">
            <div className="border-b border-white/10 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-black uppercase tracking-wider text-yellow-400">
                    Wolf Media • Hesap Ekstresi
                  </div>
                  <h3 className="text-xl font-black text-white mt-1">
                    {ledgerModal.publisher?.publisherName || ledgerModal.publisher?.publisherUsername}
                  </h3>
                  <div className="text-xs text-white/50 mt-0.5">
                    @{ledgerModal.publisher?.publisherUsername}
                    {ledgerModal.publisher?.hiddenId && ` • ID: ${ledgerModal.publisher?.hiddenId}`}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setLedgerModal({ open: false, publisher: null, ledger: [], loading: false, currentTotalBalance: 0 })}
                  className="rounded-lg border border-white/10 bg-white/[0.04] p-2 text-white/50 hover:text-white"
                >
                  ✕
                </button>
              </div>

              <div className="mt-4 rounded-xl border border-yellow-500/30 bg-yellow-500/10 p-3 flex items-center justify-between">
                <span className="text-xs font-bold text-yellow-300">GÜNCEL TOPLAM BAKİYE:</span>
                <span
                  className={`text-base font-black ${
                    ledgerModal.currentTotalBalance >= 0 ? "text-yellow-300" : "text-red-400"
                  }`}
                >
                  {ledgerModal.currentTotalBalance > 0 ? "+" : ""}
                  💎 {formatNumber(ledgerModal.currentTotalBalance)} Elmas
                </span>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto py-4">
              {ledgerModal.loading ? (
                <div className="py-16 text-center text-white/40">
                  <span className="animate-spin inline-block mr-2">🔄</span>
                  Hesap ekstresi yükleniyor...
                </div>
              ) : ledgerModal.ledger.length === 0 ? (
                <div className="py-16 text-center text-white/40 text-xs">
                  Bu yayıncıya ait kayıtlı hakediş veya gönderim hareketi bulunamadı.
                </div>
              ) : (
                <table className="w-full border-collapse text-left text-xs">
                  <thead>
                    <tr className="border-b border-white/10 text-white/40 text-[10px] uppercase">
                      <th className="py-2.5 px-3">Tarih / Dönem</th>
                      <th className="py-2.5 px-3">İşlem Türü</th>
                      <th className="py-2.5 px-3 text-right">Tutar</th>
                      <th className="py-2.5 px-3 text-right">Yürüyen Bakiye</th>
                      <th className="py-2.5 px-3">Açıklama</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {ledgerModal.ledger.map((item, idx) => {
                      const isAward = item.type === "AWARD";
                      const isCancelled = item.isCancelled;

                      return (
                        <tr
                          key={`${item.type}-${item.id}-${idx}`}
                          className={`hover:bg-white/[0.02] ${isCancelled ? "opacity-45" : ""}`}
                        >
                          <td className="py-3 px-3">
                            <div className="font-bold text-white/80">{item.transactionDate}</div>
                            <div className="text-[10px] text-white/40">{periodLabel(item.periodMonth)}</div>
                          </td>

                          <td className="py-3 px-3">
                            {isAward ? (
                              <span className="inline-flex items-center gap-1 rounded-md border border-yellow-500/25 bg-yellow-500/10 px-2 py-0.5 text-[10px] font-bold text-yellow-300">
                                💎 Hakediş
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 rounded-md border border-emerald-500/25 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold text-emerald-300">
                                🎁 Gönderim
                              </span>
                            )}
                            {isCancelled && (
                              <span className="ml-1 text-[10px] text-red-400 font-bold">(İptal)</span>
                            )}
                          </td>

                          <td className="py-3 px-3 text-right font-black">
                            {isCancelled ? (
                              <span className="line-through text-white/30">
                                {formatNumber(Math.abs(item.amount))}
                              </span>
                            ) : item.amount > 0 ? (
                              <span className="text-yellow-300">+{formatNumber(item.amount)}</span>
                            ) : (
                              <span className="text-emerald-300">-{formatNumber(Math.abs(item.amount))}</span>
                            )}
                          </td>

                          <td className="py-3 px-3 text-right font-black">
                            <span
                              className={
                                item.runningBalance > 0
                                  ? "text-yellow-300"
                                  : item.runningBalance < 0
                                  ? "text-red-400"
                                  : "text-zinc-400"
                              }
                            >
                              {item.runningBalance > 0 ? `+${formatNumber(item.runningBalance)}` : formatNumber(item.runningBalance)}
                            </span>
                          </td>

                          <td className="py-3 px-3 text-white/60">
                            <div>{item.description || "-"}</div>
                            <div className="text-[10px] text-white/30">Yönetici: {item.createdBy}</div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            <div className="border-t border-white/10 pt-4 flex justify-end">
              <button
                type="button"
                onClick={() => setLedgerModal({ open: false, publisher: null, ledger: [], loading: false, currentTotalBalance: 0 })}
                className="rounded-xl border border-white/10 px-5 py-2.5 text-xs font-bold text-white/70 hover:text-white"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
